import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MdEdit, MdPersonSearch } from "react-icons/md";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import AtriumApi, { getApiErrorMessage } from "api";
import Button from "components/button";
import Card from "components/card";
import { PageContainer, PageHeader } from "components/page";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import Tabs, { TabPanel } from "components/tabs";
import PersonHeader from "./components/PersonHeader";
import { PERSON_TABS, PersonTabContent } from "./user-details";

const BACK = { backTo: "/admin/people", backLabel: "Back to People" };

/**
 * Full person profile: header with avatar and badges, then the profile tabs.
 */
const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    if (id) {
      track(EVENTS.MEMBER_PROFILE_VIEWED, { member_id: id });
    }
  }, [id]);

  const loadUser = useCallback(() => {
    setLoading(true);
    setError(null);
    AtriumApi.get(`/v1/users/get-user/${id}`)
      .then((response) => {
        setUser(response?.data?.user || null);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!id) {
      navigate("/admin/people");
      return;
    }
    loadUser();
  }, [id, navigate, loadUser]);

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Profile" {...BACK} />
        <Card padding="md" aria-busy="true">
          <div className="flex items-center gap-4">
            <Skeleton className="h-14 w-14 rounded-full" />
            <Skeleton lines={2} className="w-64" />
          </div>
          <Skeleton lines={4} className="mt-6" />
        </Card>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Profile" {...BACK} />
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(error, "This profile could not be loaded.")}
            onRetry={loadUser}
          />
        </Card>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer>
        <PageHeader title="Profile" {...BACK} />
        <Card padding="md">
          <EmptyState
            icon={MdPersonSearch}
            title="Person Not Found"
            description="This person may have been deleted or you may not have access."
            action={
              <Button as={Link} to="/admin/people" variant="secondary">
                Back to People
              </Button>
            }
          />
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PersonHeader
        user={user}
        {...BACK}
        actions={
          <Button
            as={Link}
            to={`/admin/people/${id}/edit`}
            leftIcon={<MdEdit className="h-4 w-4" />}
          >
            Edit Profile
          </Button>
        }
      />
      <Card padding="md" className="gap-5">
        <Tabs
          id="person-tabs"
          aria-label="Person sections"
          syncParam="tab"
          tabs={PERSON_TABS}
          value={activeTab}
          onChange={setActiveTab}
        />
        <TabPanel tabsId="person-tabs" tabId={activeTab}>
          <PersonTabContent tab={activeTab} user={user} />
        </TabPanel>
      </Card>
    </PageContainer>
  );
};

export default UserProfile;
